import { useState } from 'react';

import { CountPill, HorizontalMenuTabs, LabeledFilterSelect } from '@backrs_ui/common-ui';

import keyLeversBanner from '@backrs_ui/grow-ui/src/assets/images/key-levers/key-levers-banner.jpg';
import { GuidePageShell } from '@backrs_ui/grow-ui/src/components/GuidePage';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { KeyLeverCard } from './components/KeyLeverCard';
import {
  KEY_LEVER_GRADES,
  KEY_LEVER_PRIORITIES,
  type KeyLeverGradeKey,
  type KeyLeverPriority,
  keyLeversStrings,
} from './keyLevers.content';
import { leverCountForPriority, leverSectionsFor } from './keyLevers.utils';
import { useAgentContext } from './KeyLeversPageContainer.agentContext';

const KEY_LEVERS_TOOL_ID = 'key_levers';

const DEFAULT_GRADE: KeyLeverGradeKey = 'g11';

const PRIORITY_TABS_ID = 'key-levers-priority-tabs';

export const KeyLeversPageContainer = () => {
  const [grade, setGrade] = useState<KeyLeverGradeKey>(DEFAULT_GRADE);
  const [priority, setPriority] = useState<KeyLeverPriority>('Primary');

  const gradeLabel = KEY_LEVER_GRADES.find(({ key }) => key === grade)?.label ?? '';
  const sections = leverSectionsFor(grade, priority);

  useAgentContext({ gradeLabel, priority, sections });

  return (
    <GuidePageShell
      toolId={KEY_LEVERS_TOOL_ID}
      banner={{
        src: keyLeversBanner,
        alt: keyLeversStrings.bannerAlt,
        hideOverlays: true,
        backgroundColor: 'Beige/25',
        artworkPosition: 'right',
      }}
    >
      <Box sx={pageSx}>
        <Box sx={titleSx}>
          <Typography variant="display-sm-semibold" component="h1">
            {keyLeversStrings.title}
          </Typography>
          <Typography
            variant="text-md-regular"
            color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
          >
            {keyLeversStrings.subtitle}
          </Typography>
        </Box>

        <Box sx={controlsSx}>
          <Box sx={gradeSelectSx}>
            <LabeledFilterSelect
              id="key-levers-grade-select"
              label={keyLeversStrings.gradeLabel}
              value={grade}
              options={KEY_LEVER_GRADES.map(({ key, label }) => ({ value: key, label }))}
              onChange={(value) => setGrade(value as KeyLeverGradeKey)}
            />
          </Box>
          <Box sx={priorityTabsScrollSx}>
            <HorizontalMenuTabs
              id={PRIORITY_TABS_ID}
              variant="box"
              tabs={[...KEY_LEVER_PRIORITIES]}
              value={KEY_LEVER_PRIORITIES.indexOf(priority)}
              setValue={(index) => setPriority(KEY_LEVER_PRIORITIES[index])}
              tabIndicators={KEY_LEVER_PRIORITIES.map((tabPriority) => (
                <CountPill key={tabPriority} value={leverCountForPriority(grade, tabPriority)} />
              ))}
            />
          </Box>
        </Box>

        <Box
          role="tabpanel"
          aria-labelledby={`${PRIORITY_TABS_ID}-tab-${priority.toLowerCase()}`}
          sx={sectionsSx}
        >
          {sections.map((section) => (
            <Box component="section" key={section.key} sx={sectionSx}>
              <Box sx={sectionHeadingSx}>
                <Typography variant="text-xl-semibold" component="h2">
                  {section.title}
                </Typography>
                <CountPill value={section.levers.length} />
              </Box>
              <Box sx={gridSx}>
                {section.levers.map((lever) => (
                  <KeyLeverCard key={`${grade}-${lever.id}`} lever={lever} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </GuidePageShell>
  );
};

const pageSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
  pt: backrs.spacing['spacing-xl'],
  containerType: 'inline-size',
});

const controlsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 700px)': {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: backrs.spacing['spacing-md'],
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

// Hardcoded: fits the longest grade name ("2nd Year College") without truncating.
const gradeSelectSx: SxProps<Theme> = {
  flex: '0 0 auto',
  width: '180px',
};

// The four tabs with their counts are wider than a phone, so the strip scrolls instead of the page.
const priorityTabsScrollSx: SxProps<Theme> = {
  maxWidth: '100%',
  overflowX: 'auto',
};

const sectionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-4xl'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const sectionHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'start',
  gap: backrs.spacing['spacing-xl'],
  '@container (min-width: 480px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
});
